import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface TeamMember {
  user_id: string;
  full_name: string;
  email: string;
  department: string | null;
  status: string;
  created_at: string;
  avatar_url: string | null;
  role: string;
  role_definition_id: string | null;
  role_definition_slug: string | null;
  role_definition_name: string | null;
  role_level: number;
}

export function useTeamMembers() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['team-members', orgId],
    queryFn: async () => {
      const [profilesRes, rolesRes, roleDefsRes] = await Promise.all([
        supabase.from('hire_profiles').select('*').eq('org_id', orgId!),
        supabase.from('hire_user_roles').select('user_id, role, role_definition_id').eq('org_id', orgId!),
        supabase.from('role_definitions').select('*'),
      ]);

      if (profilesRes.error) throw profilesRes.error;
      if (rolesRes.error) throw rolesRes.error;

      const roleDefs = roleDefsRes.data || [];

      return (profilesRes.data || []).map((p): TeamMember => {
        const userRole = rolesRes.data?.find(r => r.user_id === p.user_id);
        const roleDef = userRole?.role_definition_id
          ? roleDefs.find(rd => rd.id === userRole.role_definition_id)
          : roleDefs.find(rd => rd.slug === userRole?.role);

        return {
          user_id: p.user_id,
          full_name: p.full_name,
          email: p.email,
          department: p.department || null,
          status: p.status,
          created_at: p.created_at,
          avatar_url: p.avatar_url,
          role: userRole?.role || 'viewer',
          role_definition_id: userRole?.role_definition_id || roleDef?.id || null,
          role_definition_slug: roleDef?.slug || userRole?.role || null,
          role_definition_name: roleDef?.name || userRole?.role || null,
          role_level: roleDef?.level || 0,
        };
      });
    },
    enabled: !!orgId,
  });
}

export function useInviteTeamMember() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      email: string;
      full_name: string;
      role_slug: string;
      department?: string;
      temp_password: string;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Unauthorized');
      }

      const { data, error } = await supabase.functions.invoke('manage-team', {
        body: { action: 'invite', ...payload },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members'] });
      toast({ title: 'Team member invited successfully' });
    },
    onError: (e: Error) => {
      toast({ title: 'Invite failed', description: e.message, variant: 'destructive' });
    },
  });
}

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      target_user_id: string;
      role_slug?: string;
      department?: string;
      full_name?: string;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Unauthorized');
      }

      const { data, error } = await supabase.functions.invoke('manage-team', {
        body: { action: 'update_role', ...payload },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members'] });
      toast({ title: 'Team member updated' });
    },
    onError: (e: Error) => {
      toast({ title: 'Update failed', description: e.message, variant: 'destructive' });
    },
  });
}

export function useDeactivateTeamMember() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: { target_user_id: string; reactivate?: boolean }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Unauthorized');
      }

      const { data, error } = await supabase.functions.invoke('manage-team', {
        body: { action: payload.reactivate ? 'reactivate' : 'deactivate', target_user_id: payload.target_user_id },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['team-members'] });
      toast({ title: vars.reactivate ? 'User reactivated' : 'User deactivated' });
    },
    onError: (e: Error) => {
      toast({ title: 'Action failed', description: e.message, variant: 'destructive' });
    },
  });
}

export function useUpdatePermissionOverrides() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      target_user_id: string;
      overrides: { domain: string; action: string; scope: string; granted: boolean }[];
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Unauthorized');
      }

      const { data, error } = await supabase.functions.invoke('manage-team', {
        body: { action: 'update_overrides', ...payload },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-overrides'] });
      qc.invalidateQueries({ queryKey: ['user-permissions'] });
      toast({ title: 'Permission overrides saved' });
    },
    onError: (e: Error) => {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    },
  });
}

export function useAuditLogs() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['audit-logs', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('org_id', orgId!)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });
}
